/**
 * CharacterSheetFull component - displays a large character sheet with portrait.
 * Styled as a fantasy parchment character sheet for the character detail page.
 */

import { useState, useRef } from 'react';
import type { Character, StatWithLabel, SkillWithLabel } from '@/types/characters';
import { STAT_KEYS, SKILL_KEYS } from '@/types/characters';
import { HealthBar } from './HealthBar';

interface CharacterSheetFullProps {
    character: Character;
    canEdit: boolean;
    onLevelUp?: () => void;
}

/**
 * Moth stamp SVG for Celestial Moth characters.
 */
function MothStamp() {
    return (
        <div className="absolute -top-3 -right-3 rotate-12 pointer-events-none z-10">
            <div className="relative" style={{ width: '90px', height: '90px' }}>
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: 'radial-gradient(circle, rgba(128, 90, 213, 0.25) 0%, rgba(128, 90, 213, 0.15) 60%, transparent 70%)',
                        border: '2px dashed rgba(128, 90, 213, 0.4)',
                    }}
                />
                <svg
                    viewBox="0 0 100 100"
                    className="absolute inset-0 w-full h-full"
                    style={{ filter: 'drop-shadow(0 0 2px rgba(128, 90, 213, 0.5))' }}
                >
                    <ellipse cx="50" cy="55" rx="6" ry="18" fill="rgba(128, 90, 213, 0.6)" />
                    <circle cx="50" cy="32" r="7" fill="rgba(128, 90, 213, 0.6)" />
                    <path d="M45 28 Q40 18 35 15 M55 28 Q60 18 65 15" stroke="rgba(128, 90, 213, 0.6)" strokeWidth="2" fill="none" strokeLinecap="round" />
                    <path d="M44 45 Q20 25 15 45 Q25 60 44 55" fill="rgba(128, 90, 213, 0.5)" />
                    <path d="M56 45 Q80 25 85 45 Q75 60 56 55" fill="rgba(128, 90, 213, 0.5)" />
                    <path d="M44 58 Q25 65 25 78 Q40 75 46 65" fill="rgba(128, 90, 213, 0.4)" />
                    <path d="M56 58 Q75 65 75 78 Q60 75 54 65" fill="rgba(128, 90, 213, 0.4)" />
                    <circle cx="30" cy="45" r="5" fill="rgba(180, 140, 255, 0.4)" />
                    <circle cx="70" cy="45" r="5" fill="rgba(180, 140, 255, 0.4)" />
                </svg>
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-purple-600/50 text-[7px] tracking-widest font-serif" style={{ fontVariant: 'small-caps' }}>
                    CELESTIAL
                </div>
            </div>
        </div>
    );
}

/**
 * Get color for stat value.
 */
function getStatColor(value: number): string {
    if (value >= 40) return 'text-emerald-700';
    if (value >= 25) return 'text-blue-800';
    if (value >= 15) return 'text-amber-800';
    return 'text-stone-500';
}

const STAT_DISPLAY: Record<string, string> = {
    head: 'Head',
    eyes: 'Eyes',
    mouth: 'Mouth',
    arms: 'Arms',
    body: 'Body',
    legs: 'Legs',
};

const SKILL_DISPLAY: Record<string, string> = {
    combat: 'Combat',
    magic: 'Magic',
    crafting: 'Crafting',
    healing: 'Healing',
    persuasion: 'Persuasion',
    stealth: 'Stealth',
    observation: 'Observation',
    tea: 'Tea',
    building: 'Building',
    herbalism: 'Herbalism',
    medicine: 'Medicine',
    cooking: 'Cooking',
};

export function CharacterSheetFull({ character, canEdit, onLevelUp }: CharacterSheetFullProps) {
    const [isUploadingPortrait, setIsUploadingPortrait] = useState(false);
    const [portraitUrl, setPortraitUrl] = useState(character.portrait_url);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const stats = character.stats;
    const skills = character.skills;

    const handlePortraitClick = () => {
        if (canEdit && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handlePortraitUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingPortrait(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            const response = await fetch(`/character/${character.id}/portrait`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
                body: formData,
                credentials: 'same-origin',
            });

            if (response.ok) {
                const data = await response.json();
                setPortraitUrl(data.portrait_url);
            }
        } catch (err) {
            console.error('Failed to upload portrait:', err);
        } finally {
            setIsUploadingPortrait(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div
            className="relative p-6 rounded-sm overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                border: '1px solid #c9b896',
            }}
        >
            {/* Aged edge effect */}
            <div
                className="absolute top-0 left-0 right-0 h-3 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)' }}
            />

            {/* Moth stamp for Celestial Moth */}
            {character.is_primary && <MothStamp />}

            {/* Hidden file input for portrait upload */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePortraitUpload}
            />

            {/* Main layout: Portrait left, info right */}
            <div className="flex gap-6">
                {/* Large Portrait area - Polaroid style */}
                <div
                    className={`flex-shrink-0 relative ${canEdit ? 'cursor-pointer group' : ''}`}
                    onClick={handlePortraitClick}
                    style={{
                        transform: 'rotate(-2deg)',
                        marginTop: '8px',
                    }}
                >
                    {/* Polaroid frame */}
                    <div
                        className="relative"
                        style={{
                            background: 'linear-gradient(145deg, #fefefe 0%, #f5f5f0 50%, #ebebdf 100%)',
                            padding: '10px 10px 36px 10px',
                            boxShadow: '3px 4px 12px rgba(0,0,0,0.25), 0 0 1px rgba(0,0,0,0.1)',
                            borderRadius: '2px',
                        }}
                    >
                        {/* Photo area */}
                        <div
                            className="relative overflow-hidden"
                            style={{
                                width: '200px',
                                height: '240px',
                                background: '#1a1a1a',
                            }}
                        >
                            {portraitUrl ? (
                                <img
                                    src={portraitUrl}
                                    alt={`Portrait of ${character.name}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                                    <svg className="w-14 h-14 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {canEdit && (
                                        <span className="text-xs">Click to add</span>
                                    )}
                                </div>
                            )}
                            {isUploadingPortrait && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                    <span className="text-white text-sm">Uploading...</span>
                                </div>
                            )}
                            {canEdit && !isUploadingPortrait && (
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <span className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">
                                        Change Portrait
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Polaroid subtle texture/aging */}
                        <div
                            className="absolute inset-0 pointer-events-none rounded-sm"
                            style={{
                                background: 'linear-gradient(135deg, transparent 40%, rgba(0,0,0,0.02) 100%)',
                            }}
                        />
                    </div>

                    {/* Tape pieces */}
                    {/* Top-left tape */}
                    <div
                        className="absolute -top-2 -left-1 w-10 h-5 pointer-events-none"
                        style={{
                            background: 'linear-gradient(180deg, rgba(210,190,140,0.85) 0%, rgba(195,175,125,0.8) 100%)',
                            transform: 'rotate(-15deg)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            borderRadius: '1px',
                        }}
                    />
                    {/* Top-right tape */}
                    <div
                        className="absolute -top-2 -right-1 w-10 h-5 pointer-events-none"
                        style={{
                            background: 'linear-gradient(180deg, rgba(210,190,140,0.85) 0%, rgba(195,175,125,0.8) 100%)',
                            transform: 'rotate(12deg)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            borderRadius: '1px',
                        }}
                    />
                </div>

                {/* Right side: Name, class, level, XP and stats */}
                <div className="flex-1 min-w-0">
                    {/* Name and basic info */}
                    <div className="mb-4">
                        <h1 className="text-3xl font-serif font-bold text-amber-900 mb-1 leading-tight">
                            {character.name}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className="text-lg font-serif text-amber-800">
                                {character.class.label}
                            </span>
                            <span className="text-amber-700/50 text-lg font-bold">|</span>
                            <span className="text-lg font-serif text-amber-800">
                                Level {character.level ?? 0}
                            </span>
                            <span className="text-amber-700/50 text-lg font-bold">|</span>
                            <span className="text-lg font-serif font-semibold text-amber-900">
                                {character.xp.toLocaleString()} XP
                            </span>
                        </div>

                        {/* XP Progress bar */}
                        <div className="mb-3">
                            <div className="h-2 rounded-full bg-amber-900/20 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-amber-600 to-amber-500 rounded-full transition-all"
                                    style={{ width: `${character.xp_progress}%` }}
                                />
                            </div>
                            {character.xp_for_next_level && (
                                <div className="text-xs font-serif text-amber-800/60 mt-1">
                                    {character.xp_for_next_level.toLocaleString()} XP for next level
                                </div>
                            )}
                        </div>

                        {/* Level up button */}
                        {(character.can_level_up || character.has_pending_points) && onLevelUp && (
                            <button
                                onClick={onLevelUp}
                                className="px-4 py-1.5 rounded font-serif font-semibold text-sm transition-all hover:scale-105"
                                style={{
                                    background: character.can_level_up
                                        ? 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'
                                        : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                    color: '#000',
                                    border: '1px solid rgba(0,0,0,0.2)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                }}
                            >
                                {character.can_level_up ? 'Level Up!' : 'Assign Points'}
                            </button>
                        )}
                    </div>

                    {/* Compact Stats and Skills side by side */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Attributes - compact */}
                        <div>
                            <h2 className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-2 font-semibold border-b border-amber-900/20 pb-1">
                                Attributes
                            </h2>
                            <div className="space-y-1">
                                {STAT_KEYS.map((key) => {
                                    const stat = stats[key] as StatWithLabel | undefined;
                                    const value = stat?.value ?? 0;
                                    const equipment = stat?.equipment ?? 0;
                                    const label = STAT_DISPLAY[key];
                                    return (
                                        <div key={key} className="flex items-baseline gap-1.5">
                                            <span className={`text-lg font-serif font-bold w-7 text-right ${getStatColor(value)}`}>
                                                {value}
                                            </span>
                                            {equipment > 0 && (
                                                <span className="text-xs font-serif font-semibold text-emerald-600">
                                                    +{equipment}
                                                </span>
                                            )}
                                            <span className="text-sm font-serif text-amber-800/70">
                                                {label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Skills - compact 2 columns */}
                        <div>
                            <h2 className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-2 font-semibold border-b border-amber-900/20 pb-1">
                                Skills
                            </h2>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                {SKILL_KEYS.map((key) => {
                                    const skill = skills[key] as SkillWithLabel | undefined;
                                    const value = skill?.value ?? 0;
                                    const equipment = skill?.equipment ?? 0;
                                    const label = SKILL_DISPLAY[key];
                                    return (
                                        <div key={key} className="flex items-baseline gap-1">
                                            <span className={`text-base font-serif font-bold ${getStatColor(value)}`}>
                                                {value}
                                            </span>
                                            {equipment > 0 && (
                                                <span className="text-xs font-serif text-emerald-600">
                                                    +{equipment}
                                                </span>
                                            )}
                                            <span className="text-xs font-serif text-amber-800/60 truncate">
                                                {label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Health and Inventory - compact */}
                    <div className="mt-4 pt-3 border-t border-amber-900/20">
                        {/* Health bar */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-xs font-serif text-amber-700/80 uppercase tracking-wider font-semibold">
                                    Health
                                </h2>
                                {character.health.is_incapacitated && (
                                    <span className="text-xs font-serif font-bold text-red-600 uppercase">
                                        Incapacitated
                                    </span>
                                )}
                            </div>
                            <HealthBar health={character.health} size="large" />
                        </div>

                        {/* Inventory slots */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-lg font-serif font-bold text-amber-900">
                                    {character.inventory_slots.used}/{character.inventory_slots.total}
                                </span>
                                <span className="text-sm font-serif text-amber-800/70">
                                    Inventory
                                </span>
                            </div>
                            <div className="flex gap-1">
                                {Array.from({ length: character.inventory_slots.total }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="w-5 h-5 rounded border"
                                        style={{
                                            borderColor: i < character.inventory_slots.used ? '#8B4513' : '#c9b896',
                                            background: i < character.inventory_slots.used
                                                ? 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)'
                                                : 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 100%)',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CharacterSheetFull;
